"use client"

import {
  addCampaign,
  deleteCampaign,
  fetchCampaignId,
  fetchCampaigns,
  updateCampaign,
} from "@/(admin)/modules/campaign/services/campaignService"
import { useCheckPermission } from "@/hook/use-check-permission"
import { t } from "@/languages/i18n"
import { PaginationMeta } from "@/types/event"
import { CampaignData, CampaignFieldType, CampaignPayload, StrapiPermissions } from "@/types/types"
import { generateDuplicateName } from "@/utils/generate-duplicate-name"
import { App, FormInstance } from "antd"
import { useRouter } from "next/navigation"
import queryString from "query-string"
import { useCallback, useEffect, useRef, useState } from "react"

interface StrapiRelationItem {
  id: number
}

export const useCampaigns = () => {
  const router = useRouter()
  const { message, modal } = App.useApp()

  const [dataItem, setDataItem] = useState<CampaignData[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [bulkAction, setBulkAction] = useState<string>("")
  const [needRefresh, setNeedRefresh] = useState<boolean>(false)
  const [permissions, setPermissions] = useState<StrapiPermissions>()
  // Guards: reqSeq drops stale responses, inFlightRequestKeyRef collapses the
  // React 19 StrictMode double-invoke into one request.
  const reqSeq = useRef(0)
  const inFlightRequestKeyRef = useRef<string | null>(null)
  const [pagination, setPagination] = useState<PaginationMeta>({
    page: 1,
    pageSize: 20,
    total: 0,
    pageCount: 0,
  })

  useEffect(() => {
    if (typeof window !== "undefined") {
      const storedPermissions = JSON.parse(localStorage.getItem("permissions") || "{}")
      setPermissions(storedPermissions)
    }
  }, [])

  const { hasPermission } = useCheckPermission()

  const fetchCampaignsData = useCallback(
    async (searchTerms?: Record<string, unknown>) => {
      const params = {
        page: String(pagination.page),
        pageSize: String(pagination.pageSize),
        ...searchTerms,
      }
      const requestKey = JSON.stringify(params)
      if (inFlightRequestKeyRef.current === requestKey) {
        return
      }
      inFlightRequestKeyRef.current = requestKey
      const seq = ++reqSeq.current
      setLoading(true)
      try {
        const response = await fetchCampaigns(params)
        if (seq !== reqSeq.current) return // superseded by a newer request
        if (response && response.data) {
          setDataItem(response.data)
          // Only page/total/pageCount — pageSize stays owned by viewConfig (RC2).
          if (response.meta?.pagination) {
            const p = response.meta.pagination
            setPagination(prev => ({
              ...prev,
              page: p.page ?? prev.page,
              total: p.total ?? 0,
              pageCount: p.pageCount ?? 0,
            }))
          }
        } else {
          setDataItem([])
        }
      } catch (error) {
        message.error("Không thể tải dữ liệu")
      } finally {
        if (inFlightRequestKeyRef.current === requestKey) {
          inFlightRequestKeyRef.current = null
        }
        if (seq === reqSeq.current) setLoading(false)
      }
    },
    [message, pagination.page, pagination.pageSize],
  )

  const fetchCampaignByIdAndSetForm = useCallback(
    async (id: number, form?: FormInstance) => {
      try {
        const response = await fetchCampaignId(id)
        if (!response || !response.attributes) {
          message.error("Không tìm thấy chiến dịch")
          return { campaign: null, formData: null }
        }

        const attr = response.attributes
        const formData: CampaignFieldType = {
          name: attr.name || "",
          dateFrom: attr.dateFrom || "",
          dateTo: attr.dateTo || "",
          tamdaExpressCampaign: attr.tamdaExpressCampaign ?? false,
          relatedProducts: attr.relatedProducts || "",
          tamdaStores: attr.tamdaStores?.data?.map((item: StrapiRelationItem) => item.id) || [],
          posts: attr.posts?.data?.map((item: StrapiRelationItem & { attributes?: { title?: string; slug?: string } }) => ({
            id: item.id,
            title: item.attributes?.title || "",
            slug: item.attributes?.slug || "",
          })) || [],
          // Fetch đầy đủ thông tin campaign_subscribers (name)
          campaign_subscribers: attr.campaign_subscribers?.data?.map((item: StrapiRelationItem & { attributes?: { fullname?: string; phone?: string } }) => ({
            id: item.id,
            fullname: item.attributes?.fullname || "",
            phone: item.attributes?.phone || "",
          })) || [],
        }

        if (form) form.setFieldsValue(formData)
        return { campaign: response, formData }
      } catch (error) {
        message.error("Lỗi khi xoá chiến dịch")
        return { campaign: null, formData: null }
      }
    },
    [message],
  )

  const handleUpsertCampaign = useCallback(
    async (values: CampaignFieldType & { publishedAt?: string | null }, id?: number, silent: boolean = false) => {
      const payload: CampaignPayload = {
        data: {
          name: values.name,
          dateFrom: values.dateFrom,
          dateTo: values.dateTo,
          tamdaExpressCampaign: !!values.tamdaExpressCampaign,
          relatedProducts: values.relatedProducts || "",
          tamdaStores: values.tamdaStores || [],
          posts: values.posts || [],
          campaign_subscribers: values.campaign_subscribers || [],
          publishedAt: values.publishedAt,
        },
      }

      try {
        if (id) {
          await updateCampaign(id, payload)
          if (!silent) {
            message.success(t("Cập nhật chiến dịch thành công!"))
          }
          return { success: true, id }
        } else {
          const res = await addCampaign(payload)
          if (!silent) {
            message.success(t("Tạo chiến dịch thành công!"))
          }
          return { success: true, id: res.data.id }
        }
      } catch {
        if (!silent) {
          message.error(t("Lưu thất bại. Vui lòng thử lại."))
        }
        return { success: false }
      }
    },
    [message],
  )

  const handleDuplicate = useCallback(
    async (campaign: CampaignData, currentParams?: Record<string, string>) => {
      try {
        setLoading(true)
        const existingNames = dataItem.map(item => item.attributes.name).filter((name): name is string => !!name)

        const duplicatedData: CampaignFieldType = {
          name: generateDuplicateName(campaign.attributes.name || "", existingNames),
          dateFrom: campaign.attributes.dateFrom,
          dateTo: campaign.attributes.dateTo,
          tamdaExpressCampaign: campaign.attributes.tamdaExpressCampaign ?? false,
          relatedProducts: campaign.attributes.relatedProducts || "",
          tamdaStores: campaign.attributes.tamdaStores?.data?.map((item: StrapiRelationItem) => item.id) || [],
          posts: campaign.attributes.posts?.data?.map((item: StrapiRelationItem) => item.id) || [],
          campaign_subscribers: campaign.attributes.campaign_subscribers?.data?.map((item: StrapiRelationItem) => item.id) || [],
        }


        const result = await handleUpsertCampaign({
          name: generateDuplicateName(campaign.attributes.name || "", existingNames),
          dateFrom: campaign.attributes.dateFrom,
          dateTo: campaign.attributes.dateTo,
          tamdaExpressCampaign: campaign.attributes.tamdaExpressCampaign ?? false,
          relatedProducts: campaign.attributes.relatedProducts || "",
          tamdaStores: [],
          posts: [],
          campaign_subscribers: [],
        }, undefined, true)

        if (result.success) {
          fetchCampaignsData(currentParams)
          message.success(t("Nhân bản chiến dịch thành công"))
        }
      } catch (error) {
        message.error(t("Không thể nhân bản chiến dịch"))
      } finally {
        setLoading(false)
      }
    },
    [dataItem, handleUpsertCampaign, fetchCampaignsData, message],
  )

  const handleDelete = useCallback(
    async (id: number, currentParams?: Record<string, string>) => {
      modal.confirm({
        title: t("Xác nhận xóa"),
        content: t("Bạn có chắc chắn muốn xóa chiến dịch này?"),
        onOk: async () => {
          try {
            await deleteCampaign(id)
            fetchCampaignsData(currentParams)
            message.success(t("Xóa chiến dịch thành công!"))
          } catch {
            message.error(t("Xóa thất bại"))
          }
        },
      })
    },
    [fetchCampaignsData, message],
  )

  const handleBulkDelete = useCallback(
    async (currentParams?: Record<string, string>) => {
      try {
        setLoading(true)
        const ids = selectedRowKeys.map(id => Number(id))
        await Promise.all(ids.map(id => deleteCampaign(id)))
        setSelectedRowKeys([])
        fetchCampaignsData(currentParams)
        message.success(t("Xóa nhiều chiến dịch thành công"))
      } catch {
        message.error(t("Xóa nhiều thất bại"))
      } finally {
        setLoading(false)
      }
    },
    [selectedRowKeys, fetchCampaignsData, message],
  )

  const handleApplyBulkAction = useCallback(
    (currentParams?: Record<string, string>) => {
      if (!hasPermission("campaign", "delete")) {
        message.error(t("Bạn không có quyền chọn những chiến dịch này"))
        return
      }

      if (bulkAction === "delete" && selectedRowKeys.length > 0) {
        modal.confirm({
          title: t("Xác nhận xóa"),
          content: t("Bạn có chắc chắn muốn xóa những chiến dịch này?"),
          onOk: () => handleBulkDelete(currentParams),
        })
      } else if (selectedRowKeys.length === 0) {
        message.warning(t("Vui lòng chọn chiến dịch để áp dụng hành động"))
      } else {
        message.warning(t("Vui lòng chọn hành động có sẵn"))
      }
    },
    [bulkAction, selectedRowKeys, handleBulkDelete, message],
  )

  const handleRefresh = useCallback((filterRef?: { current: { filterFunction: () => void } | null }) => {
    if (filterRef?.current) {
      filterRef.current.filterFunction()
    }
    setNeedRefresh(prev => !prev)
  }, [])

  const handleSearch = useCallback(
    (filters: Record<string, string | number | boolean>, pathname: string) => {
      const newFilters = { ...filters }

      if (newFilters.publicationState === "preview") {
        delete newFilters.publicationState
      }

      const searchParams = queryString.stringify(newFilters, { encode: false })
      router.push(searchParams ? `${pathname}?${searchParams}` : pathname)
    },
    [router],
  )

  return {
    dataItem,
    loading,
    pagination,
    selectedRowKeys,
    bulkAction,
    needRefresh,
    actions: {
      fetchCampaignsData,
      fetchCampaignByIdAndSetForm,
      handleDelete,
      handleSearch,
      handleUpsertCampaign,
      setSelectedRowKeys,
      setBulkAction,
      handleApplyBulkAction,
      handleRefresh,
      handleDuplicate,
      hasPermission,
    },
  }
}
